/**
 * Search UI DOM Event Handler. Responsible for rendering search results and handling
 * user interaction.
 */
import MessageBusTopics from '../../../../common/MessageBusTopics';

/**
 * Search UI DOM Event Handler constructor
 *
 * @param {Object} messageBus  WH message bus
 * @param {Object} dom  DOM objects
 *
 * @constructor
 */
function SearchUiDomEventHandler(dom) {

    this.dom = dom;
    this.isActualViewPortSmall = undefined;

    this.bindAllDomEvents();
}

/**
 * Binding all the user generated DOM events
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.bindAllDomEvents = function () {

    this.bindWindowKeyDownEvent();
    this.bindSearchOpenClickEvent();
    this.bindSearchCloseClickEvent();
    this.bindSearchInputChangeEvent();
    this.bindRecentSearchesItemClickEvent();
    this.bindEventResultItemClickEvent();
    this.rebindEventResultsScrollEvent();
    this.bindMarketSelectionsClickEvent();
    this.bindIconBarItemClickEvent();
    this.bindIconBarScrollEvent();
    this.bindWindowResizeEvent();
};

/**
 * Rerturn a function that triggers internal event and calls the passed handler function
 *
 * @param {String} internalEventName
 * @param {Function} eventHandlerCallBack
 *
 * @returns {Function}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.triggerInternalEventAndCallHandler = function (internalEventName,
    eventHandlerCallBack) {
    return function (event) {

        if (typeof eventHandlerCallBack === 'function') {
            eventHandlerCallBack(event);
        }

        if (internalEventName === 'search.eventResultItem.click' ||
            internalEventName === 'search.iconBarItem.click' ||
            internalEventName === 'search.close') {
            document.body.style.overflow = 'unset';
        }

        if (window.WH && window.WH.messageBus) {
            window.WH.messageBus.publish(internalEventName, event);
        }
    }.bind(this);
};

/**
 * Binds an event to the relevant DOM node and assign a handler function
 *
 * @param domEventName
 * @param internalEventName
 * @param domObject
 * @param descendantsSelectors
 * @param eventHandlerCallBack
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.bindAnEventToTheDom = function (domEventName, internalEventName, domObject,
    descendantsSelectors, eventHandlerCallBack) {

    domObject.off(domEventName);

    if (typeof descendantsSelectors !== 'undefined') {

        domObject.on(domEventName, descendantsSelectors, this.triggerInternalEventAndCallHandler(
            internalEventName,
            eventHandlerCallBack
        ));
    } else {

        domObject.on(domEventName, this.triggerInternalEventAndCallHandler(
            internalEventName,
            eventHandlerCallBack
        ));
    }
};

/**
 * Binding an event to DOM elements and triggering another internal event
 *
 * @param domEventName {String} the DOM event to bind (eg: 'click.search.close')
 * @param internalEventName {String} the internal event to trigger (eg: 'search.close')
 * @param descendant {String} dom selectors (coma separated. eg: 'p > a, li a, td > a')
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.bindAnEventToTheDomUnderOverlayAndTriggerAnotherInternalEvent =
    function (domEventName, internalEventName, descendant) {

        this.bindAnEventToTheDom(
            domEventName,
            internalEventName,
            this.dom.overlay,
            descendant.selector
        );
    };

/**
 *
 * @param event
 */
SearchUiDomEventHandler.prototype.defaultEventHandler = function (event) {

    event.preventDefault();
    event.stopPropagation();
};

/**
 * Binding the click event handlers on the Search Open buttons
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.bindSearchOpenClickEvent = function () {

    var openSearchEventHandler = function (event) {
        this.defaultEventHandler(event);
        this.dom.input.focus();
    }.bind(this);

    this.bindAnEventToTheDom(
        'click.search.open',
        'search.open',
        this.dom.open,
        undefined,
        openSearchEventHandler
    );
};

/**
 * Binding the click event handler on the Search Close button
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.bindSearchCloseClickEvent = function () {

    this.bindAnEventToTheDomUnderOverlayAndTriggerAnotherInternalEvent(
        'click.search.close',
        'search.close',
        this.dom.close
    );
};

/**
 * Binding the change event handler on Search input
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.bindSearchInputChangeEvent = function () {

    this.bindAnEventToTheDomUnderOverlayAndTriggerAnotherInternalEvent(
        'input.search.input',
        'search.input.change',
        this.dom.input
    );
};

/**
 * Binding the click event handler on Recent Searches item
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.bindRecentSearchesItemClickEvent = function () {

    this.bindAnEventToTheDom(
        'click.search.a.recentSearchesResult',
        'search.recentSearchesItem.click',
        this.dom.recentSearchesResults,
        'a',
        this.defaultEventHandler
    );
};

/**
 * Binding the click event handler on Event Result item
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.bindEventResultItemClickEvent = function () {

    this.bindAnEventToTheDom(
        'click.search.a.eventResults',
        'search.eventResultItem.click',
        this.dom.eventsResults,
        'a'
    );
};

/**
 * Binding the click event handler on Event Result item
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.bindIconBarItemClickEvent = function () {

    this.bindAnEventToTheDom(
        'click.search.a.quickLinksResults',
        'search.iconBarItem.click',
        this.dom.quickLinksResults,
        'a'
    );
};

/**
 * Unbinding and binding the scroll event handler on Event Results list or Search Overlay
 * dependent on the ViewPort.
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.rebindEventResultsScrollEvent = function () {

    var internalEventName = 'search.events.scroll';
    var domEventName = 'scroll';
    var isSmallViewPort = this.dom.isSmallViewPort();

    var isInitialLoad = function () {
        return typeof this.isActualViewPortSmall === 'undefined';
    }.bind(this);

    var hasViewPortChanged = function () {
        return this.isActualViewPortSmall !== isSmallViewPort;
    }.bind(this);

    if (isInitialLoad() || hasViewPortChanged()) {

        if (isSmallViewPort) {

            this.dom.eventsResults.off(domEventName);
            this.bindAnEventToTheDom(
                domEventName,
                internalEventName,
                this.dom.overlay
            );
        } else {

            this.dom.overlay.off(domEventName);
            this.bindAnEventToTheDom(
                domEventName,
                internalEventName,
                this.dom.eventsResults
            );
        }

        this.isActualViewPortSmall = isSmallViewPort;
    }
};

/**
 * Binding the scroll event handler on Event Results list
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.bindIconBarScrollEvent = function () {

    this.bindAnEventToTheDom(
        'scroll',
        'search.iconBar.scroll',
        this.dom.quickLinksResults
    );
};

SearchUiDomEventHandler.prototype.bindMarketSelectionsClickEvent = function () {

    this.bindAnEventToTheDom(
        'click.search.button.marketSelection',
        'search.marketSelection.click',
        this.dom.eventsResults,
        'button'
    );
};

/**
 * Binding the key down event handler on the window
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.bindWindowKeyDownEvent = function () {

    var internalKeyDownEventName = 'search.window.keydown';
    var internalCloseEventName = 'search.close';
    var ESCAPE_KEY = 27;

    this.dom.document.on('keydown.search.document', function (keyDownEvent) {

        if (window.WH && window.WH.messageBus) {
            window.WH.messageBus.publish(internalKeyDownEventName, keyDownEvent);

            if (keyDownEvent && keyDownEvent.keyCode === ESCAPE_KEY) {
                window.WH.messageBus.publish(internalCloseEventName, keyDownEvent);
            }
        }
    }.bind(this));
};

/**
 * Binding resize event handler on the windows
 *
 * @returns {void}
 *
 * @private
 */
SearchUiDomEventHandler.prototype.bindWindowResizeEvent = function () {

    window.onresize = function (event) {

        this.rebindEventResultsScrollEvent.apply(this);
        if (window.WH && window.WH.messageBus) {
            window.WH.messageBus.publish(MessageBusTopics.SEARCH_WINDOW_RESIZE, event);
        }
    }.bind(this);
};

export default SearchUiDomEventHandler;
